import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { CintaDeVersionNueva } from '../../../nucleo/pwa/CintaDeVersionNueva.jsx';
import { obtenerPrestamo } from '../api/prestamosApi.js';
import { CalendarioDelPrestamo } from '../componentes/CalendarioDelPrestamo.jsx';
import { ASPECTO_DE_LA_ETIQUETA, importe as formatearImporte } from '../formato.js';

/**
 * EL PRÉSTAMO CON SU PLAN DE PAGOS (P-20-02, RF-270), que es TAMBIÉN la pantalla de
 * la hipoteca año por año (P-20-08) y la del préstamo sin plan todavía (P-20-06).
 *
 * ES UNA SOLA PANTALLA Y ES DELIBERADO: lo que cambia entre las tres es el DATO, no
 * la pantalla. Con 24 pagos o menos el calendario se pinta completo y con más se
 * pliega por año —el pliegue es PRESENTACIÓN (RN-776, D-451)—, y sin plan generado
 * se enseña el estado vacío con lo que el plan aporta (RN-778, D-457). Tres rutas
 * para el mismo detalle habrían sido tres sitios donde arreglar la misma regla.
 *
 * TODO LO QUE SE ENSEÑA SE DERIVA AL CONSULTAR Y NADA ESTÁ GUARDADO (RNF-137): la
 * deuda la calculan los traspasos registrados (D-52), el contador avanza SOLO al
 * pasar cada fecha —sin ningún proceso que escriba nada— y el reparto de cada pago
 * se lee de sus dos movimientos (D-435).
 *
 * LOS DOS CONTADORES SE ENSEÑAN A LA VEZ Y NO SE CONTRADICEN (RN-779, D-458,
 * CA-20-31): el AVANCE del plan dice cuántos pagos ya cargó el calendario —14 de 48
 * con el pago 15 registrado y su fecha por llegar (RN-777)— y el «pago que sigue»
 * lleva la IDENTIDAD de esa mensualidad —15/48—. Responden preguntas distintas:
 * «¿cuántos ya cargó?» y «¿cuál toca?». Si alguien los unifica, uno miente.
 *
 * NINGUNA ACCIÓN DE TARJETA NI DE MESES SIN INTERESES SE OFRECE AQUÍ (RN-752,
 * D-440): ni liquidar por anticipado, ni aplicar devolución, ni descontar lo
 * declarado, ni fechas de corte, ni renovación de plástico. Los pagos adelantados a
 * capital son del incremento 21 y aquí no existen.
 */
export default function DetalleDelPrestamo() {
  const { idCuenta } = useParams();
  const [parametros] = useSearchParams();
  const navegar = useNavigate();
  const hoy = obtenerFechaDeNegocioHoy();

  const [prestamo, setPrestamo] = useState(null);
  const [error, setError] = useState(null);

  const acuse = parametros.get('acuse');

  useEffect(() => {
    (async () => {
      try {
        setPrestamo(await obtenerPrestamo({ idCuenta: Number(idCuenta), fechaLocal: hoy }));
      } catch (fallo) {
        setError(fallo);
      }
    })();
  }, [idCuenta, hoy]);

  if (error) {
    return (
      <Shell titulo="Préstamo" volverA="/cuentas">
        <AvisoDeError error={error} />
      </Shell>
    );
  }

  if (!prestamo) {
    return (
      <Shell titulo="Préstamo" volverA="/cuentas">
        <div className="centrado"><div className="aviso i">Abriendo el préstamo…</div></div>
      </Shell>
    );
  }

  const { cuenta } = prestamo;
  const codigoISO = cuenta.codigoISO;
  const planCancelado = prestamo.claveEstadoDelPlan === 'CANCELADO';

  return (
    <Shell titulo={cuenta.nombre} volverA="/cuentas">
      <CintaDeVersionNueva />

      {acuse === 'plan' && (
        <div className="aviso ok" role="status" data-prueba="prestamo-acuse-plan">
          <b className="tit">Plan de pagos guardado</b>
          Ya aparece en lo que vence y lleva el conteo de cada pago. Los pagos que hiciste antes de
          usar la aplicación no están en la lista y no se vuelven a contar.
        </div>
      )}
      {acuse === 'pago' && (
        <div className="aviso ok" role="status" data-prueba="prestamo-acuse-pago">
          <b className="tit">Pago registrado</b>
          Tu pago quedó como dos movimientos: el capital bajó lo que debes y el interés es gasto del
          mes. <b>El conteo de pagos no se mueve por registrarlo</b>: avanza cuando pasa la fecha de
          cada pago.
        </div>
      )}
      {/* Incremento 21/21 · el acuse del abono. Repite lo que el abono NO hizo,
          porque es lo que se olvida: la mensualidad del mes sigue pendiente
          (D-467/RN-788). */}
      {acuse === 'adelanto' && (
        <div className="aviso ok" role="status" data-prueba="prestamo-acuse-adelanto">
          <b className="tit">Abono registrado</b>
          Lo que debes bajó y los pagos que te faltan ya están acomodados. Ojo: este abono no cubre
          tu mensualidad del mes, que se registra aparte.
        </div>
      )}

      {acuse === 'cancelacion' && (
        <div className="aviso w" role="status" data-prueba="prestamo-acuse-cancelacion">
          <b className="tit">Plan de pagos cancelado</b>
          Los pagos que faltaban quedaron cancelados. No cambió lo que debes, ni los pagos que ya
          registraste, ni sus gastos de intereses. Puedes volver a generar el plan cuando quieras.
        </div>
      )}

      {/* ===== LA CABECERA ================================================= */}
      <div className="card" style={{ margin: 12 }} data-prueba="prestamo-cabecera">
        <div className="imp grande" data-prueba="prestamo-deuda">
          {formatearImporte(prestamo.deudaVigente, codigoISO)}
        </div>
        <div className="ayuda">Lo que debes hoy · calculado con tus pagos registrados</div>

        {prestamo.planGenerado && (
          <>
            <div className="fila" data-prueba="prestamo-avance">
              <span className="crece">Pagos que ya se cargaron</span>
              {/* RN-779 (a) · EL AVANCE, armado por el servidor (RNF-038). */}
              <b>{prestamo.contadorDeAvance}</b>
            </div>
            <div className="fila">
              <span className="crece">Te faltan</span>
              <b data-prueba="prestamo-faltan">{prestamo.pagosQueFaltan} pagos</b>
            </div>
            <span className="ayuda" data-prueba="prestamo-ayuda-avance">
              El conteo avanza cuando pasa la fecha de cada pago, no al registrarlo.
            </span>
          </>
        )}

        <div className="fila">
          <span className="crece">Monto original</span>
          <b>{prestamo.montoOriginal ? formatearImporte(prestamo.montoOriginal, codigoISO) : '—'}</b>
        </div>
        {prestamo.capitalYaBajado && (
          <div className="fila" data-prueba="prestamo-capital-ya-bajado">
            <span className="crece">Ya le bajaste</span>
            <b>{formatearImporte(prestamo.capitalYaBajado, codigoISO)}</b>
          </div>
        )}
      </div>

      {/* ===== SIN PLAN TODAVÍA (P-20-06, RN-778, D-457) ==================== */}
      {!prestamo.planGenerado && (
        <>
          <div className="card" style={{ margin: 12 }} data-prueba="prestamo-sin-plan">
            <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>TODAVÍA NO TIENE PLAN DE PAGOS</h4>
            <span className="ayuda">
              Con el plan, el préstamo aparece en lo que vence, lleva el conteo de cada pago y la
              aplicación puede proponerte el reparto entre capital e interés. Hacen falta cuatro
              datos de tu contrato: el pago mensual, el plazo, cuántos pagos ya hiciste y la fecha
              del próximo.
            </span>

            <button
              className="btn ancho"
              type="button"
              onClick={() => navegar(`/cuentas/prestamos/${idCuenta}/plan`)}
              data-prueba="prestamo-generar-plan"
            >
              Generar el plan de pagos
            </button>
          </div>

          <div className="card" style={{ margin: 12 }} data-prueba="prestamo-sin-plan-que-pasa">
            <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>¿Y SI REGISTRO UN PAGO SIN EL PLAN?</h4>
            <span className="ayuda">
              Se puede: el capital baja lo que debes y el interés entra como gasto del mes con su
              categoría. Lo que cambia es que <b>la aplicación no puede proponerte el reparto</b>
              {' '}—no sabe de cuánto es tu pago mensual ni cuántos pagos van—, así que lo escribes
              tú. <b>Si dejas el interés en cero, ese mes no va a tener el gasto de intereses</b>,
              aunque el banco te lo haya cobrado. Mientras no tenga plan no hay calendario ni conteo
              y no aparece en lo que vence; lo que registres ahora contará solo cuando generes el
              plan.
            </span>
          </div>
        </>
      )}

      {/* ===== EL PAGO DEL MES Y SU ETIQUETA (D-438, RN-749) =============== */}
      {prestamo.planGenerado && prestamo.importeEsperadoDelPeriodo && (
        <div className="card" style={{ margin: 12 }} data-prueba="prestamo-pago-del-mes">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>EL PAGO DE ESTE MES</h4>

          <div className="fila">
            <span className="crece">
              {/* RN-779 (b) · LA IDENTIDAD de la mensualidad que sigue: 15/48. */}
              El que sigue <b data-prueba="prestamo-contador-del-siguiente">{prestamo.contadorDeMensualidad}</b>
              {prestamo.fechaDelSiguientePago && ` · ${formatearFecha(prestamo.fechaDelSiguientePago)}`}
            </span>
            {prestamo.claveEtiquetaDelPeriodo && (
              <span
                className={`chip ${ASPECTO_DE_LA_ETIQUETA[prestamo.claveEtiquetaDelPeriodo] ?? ''}`}
                data-prueba="prestamo-etiqueta-del-periodo"
              >
                {prestamo.textoEtiquetaDelPeriodo}
              </span>
            )}
          </div>
          <div className="fila">
            <span className="crece">Pago del mes</span>
            <b>{formatearImporte(prestamo.importeEsperadoDelPeriodo, codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Ya pagado este mes</span>
            <b>{formatearImporte(prestamo.importePagadoDelPeriodo, codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Falta</span>
            <b data-prueba="prestamo-pendiente-del-esperado">
              {formatearImporte(prestamo.pendienteDelEsperado, codigoISO)}
            </b>
          </div>
          <span className="ayuda">
            Esta cifra es el pago del mes, no lo que debes. Lo que debes baja solo con el capital.
          </span>
        </div>
      )}

      {/* ===== EL PAGO QUE SIGUE, POR DENTRO (D-430, RN-745) =============== */}
      {prestamo.planGenerado && prestamo.capitalPropuestoDelSiguientePago && (
        <div className="card" style={{ margin: 12 }} data-prueba="prestamo-propuesta">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>EL PAGO QUE SIGUE, POR DENTRO</h4>
          <div className="fila">
            <span className="crece">Capital</span>
            <b>{formatearImporte(prestamo.capitalPropuestoDelSiguientePago, codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Interés</span>
            <b>{formatearImporte(prestamo.interesPropuestoDelSiguientePago, codigoISO)}</b>
          </div>
          {/* El texto lo redacta el SERVIDOR y dice de dónde sale la cifra —del
              último pago o del promedio del contrato— o de dónde NO pudo salir
              (RN-745c, RNF-130). La pantalla no lo compone. */}
          <span className="ayuda" data-prueba="prestamo-propuesta-texto">{prestamo.textoDeLaPropuesta}</span>
        </div>
      )}

      {/* ===== EL RESUMEN DE LO REGISTRADO ================================= */}
      {prestamo.planGenerado && (
        <div className="card" style={{ margin: 12 }} data-prueba="prestamo-resumen">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>LO QUE LLEVAS REGISTRADO AQUÍ</h4>
          <div className="fila">
            <span className="crece">Bajó tu deuda (capital)</span>
            <b>{formatearImporte(prestamo.totalCapitalRegistrado, codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Fue interés (gasto del mes)</span>
            <b>{formatearImporte(prestamo.totalInteresRegistrado, codigoISO)}</b>
          </div>
          {prestamo.totalPorEntregar && (
            <div className="fila">
              <span className="crece">Por entregar hasta el final</span>
              <b>{formatearImporte(prestamo.totalPorEntregar, codigoISO)}</b>
            </div>
          )}
          {prestamo.nombreCategoriaDelInteres && (
            <div className="fila">
              <span className="crece">El interés va a</span>
              <b>{prestamo.nombreCategoriaDelInteres}</b>
            </div>
          )}
        </div>
      )}

      {/* ===== EL CALENDARIO DE PAGOS (RF-270, RN-776) ===================== */}
      {prestamo.planGenerado && prestamo.mensualidades.length > 0 && (
        <div className="card" style={{ margin: 12 }} data-prueba="prestamo-bloque-calendario">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>LOS PAGOS QUE TE FALTAN</h4>

          <CalendarioDelPrestamo
            mensualidades={prestamo.mensualidades}
            codigoISO={codigoISO}
            datoDePrueba="prestamo-calendario"
          />

          <span className="ayuda">
            Todos los pagos valen lo mismo: ninguno carga centavos de sobra ni es distinto al final.
            Lo que cambia mes a mes es cuánto es capital y cuánto interés, y eso lo escribes tú al
            pagar.
          </span>

          {/* D-433 · por qué los pagos viejos no están en la lista. El texto lo arma
              el servidor (RNF-130). */}
          {prestamo.textoDeLosPagosAnteriores && (
            <div className="aviso i" role="status" data-prueba="prestamo-pagos-anteriores">
              {prestamo.textoDeLosPagosAnteriores}
            </div>
          )}
        </div>
      )}

      {/* ===== EL HISTORIAL DE PAGOS REGISTRADOS (D-435, RN-748) =========== */}
      {prestamo.planGenerado || prestamo.pagos.length > 0 ? (
        <div className="card" style={{ margin: 12 }} data-prueba="prestamo-historial">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>TUS PAGOS REGISTRADOS</h4>

          {prestamo.pagos.length === 0 ? (
            <span className="ayuda" data-prueba="prestamo-historial-vacio">
              Todavía no has registrado ningún pago aquí. Cuando registres el primero, verás su
              reparto entre capital e interés y podrás abrir sus dos movimientos.
            </span>
          ) : (
            prestamo.pagos.map((pago) => (
              <div className="fila" key={pago.idPagoDePrestamo} data-prueba={`prestamo-pago-${pago.idPagoDePrestamo}`}>
                <div className="crece">
                  <b>{pago.fecha ? formatearFecha(pago.fecha) : 'Sin fecha'}</b>
                  <small>
                    capital {formatearImporte(pago.importeCapital, codigoISO)}
                    {' · '}
                    interés {formatearImporte(pago.importeInteres, codigoISO)}
                    {pago.nombreCategoriaDelInteres && ` · ${pago.nombreCategoriaDelInteres}`}
                  </small>
                  <small>
                    {pago.idMovimientoCapital && (
                      <Link to={`/movimientos/${pago.idMovimientoCapital}`} data-prueba="prestamo-ver-capital">
                        Ver el capital
                      </Link>
                    )}
                    {pago.idMovimientoCapital && pago.idMovimientoInteres && ' · '}
                    {pago.idMovimientoInteres && (
                      <Link to={`/movimientos/${pago.idMovimientoInteres}`} data-prueba="prestamo-ver-interes">
                        Ver el interés
                      </Link>
                    )}
                  </small>
                  {/* La marca de que una de las dos partes dejó de contar la redacta
                      el servidor (RNF-130): cancelar un movimiento lo saca de lo
                      pagado sin tocar el vínculo (RN-748, CA-20-18). */}
                  {pago.textoDeAdvertencia && (
                    <small data-prueba="prestamo-pago-advertencia">⚠ {pago.textoDeAdvertencia}</small>
                  )}
                </div>
                <span className="imp">{formatearImporte(pago.importeTotal, codigoISO)}</span>
              </div>
            ))
          )}

          <span className="ayuda">
            El reparto se lee de esos dos movimientos: no hay una segunda copia de la cifra. Si
            corriges el gasto del interés, el reparto se corrige solo.
          </span>
        </div>
      ) : null}

      {/* ===== EL HISTORIAL DE ABONOS A CAPITAL (RF-278, §11) =============
          Se enseña SOLO cuando hay alguno: un recuadro que dice siempre «todavía
          no» ocupa sitio sin aportar nada, y el camino para hacer el primero está
          en las acciones de abajo.

          Importe y fecha vienen DERIVADOS DEL TRASPASO (RN-789) y el renglón lo
          redacta el servidor (RNF-130); aquí no se arma ninguna frase con el modo.
          Un abono cancelado o eliminado se enseña CON SU MARCA, y lo que dice esa
          marca —que el calendario no volvió solo— es conducta declarada (D-469), no
          un defecto que haya que tapar. */}
      {(prestamo.pagosAdelantados?.length ?? 0) > 0 && (
        <div className="card" style={{ margin: 12 }} data-prueba="prestamo-historial-adelantos">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>TUS ABONOS A CAPITAL</h4>

          {prestamo.pagosAdelantados.map((adelanto) => (
            <div
              className="fila"
              key={adelanto.idPagoAdelantado}
              data-prueba={`prestamo-adelanto-${adelanto.idPagoAdelantado}`}
            >
              <div className="crece">
                <b>{adelanto.fecha ? formatearFecha(adelanto.fecha) : 'Sin fecha'}</b>
                <small>{adelanto.textoDelRenglon}</small>
                <small>
                  <Link to={`/movimientos/${adelanto.idMovimientoCapital}`} data-prueba="prestamo-ver-abono">
                    Ver el abono
                  </Link>
                </small>
                {adelanto.textoDeAdvertencia && (
                  <small data-prueba="prestamo-adelanto-advertencia">⚠ {adelanto.textoDeAdvertencia}</small>
                )}
              </div>
              <span className="imp">{formatearImporte(adelanto.importeAbono, codigoISO)}</span>
            </div>
          ))}

          <span className="ayuda">
            Un abono a capital baja lo que debes y acomoda los pagos que te faltan. No cuenta como
            el pago de su mes: esa mensualidad se registra aparte.
          </span>
        </div>
      )}

      {/* ===== LAS ACCIONES =============================================== */}
      <div className="card" style={{ margin: 12 }} data-prueba="prestamo-acciones">
        {prestamo.puedeRegistrarPago && (
          <button
            className="btn ancho"
            type="button"
            onClick={() => navegar(`/cuentas/prestamos/${idCuenta}/pagos/nuevo`)}
            data-prueba="prestamo-registrar-pago"
          >
            {prestamo.planGenerado ? 'Registrar un pago' : 'Registrar un pago sin plan'}
          </button>
        )}

        {/* §11 · `admitePagoAdelantado` es AYUDA VISUAL ANTES DE INTENTAR (mismo
            carácter que `puedeLiquidarse` del 4/6): plan vigente, deuda y al menos
            un pago por caer. EL BLOQUEO REAL LO APLICA EL SERVIDOR (RN-791); esto
            solo evita ofrecer una puerta que se cerraría en la cara. */}
        {prestamo.admitePagoAdelantado && (
          <button
            className="btn sec ancho"
            type="button"
            onClick={() => navegar(`/cuentas/prestamos/${idCuenta}/pagos-adelantados/nuevo`)}
            data-prueba="prestamo-abonar-a-capital"
          >
            Abonar a capital
          </button>
        )}

        {/* RN-753 · todos VEN el préstamo; solo el propietario opera. El motivo se
            dice, en vez de esconder el botón sin explicación. */}
        {!prestamo.esPropio && (
          <span className="ayuda" data-prueba="prestamo-ajeno">
            Este préstamo es de la otra persona del hogar: puedes verlo completo, pero sus pagos los
            registra quien es su dueño.
          </span>
        )}

        {prestamo.planGenerado && (
          <Link
            className="btn sec ancho"
            to={`/cuentas/vencimientos?idCuenta=${idCuenta}`}
            data-prueba="prestamo-ver-en-vencen"
          >
            Ver en lo que vence
          </Link>
        )}

        <Link
          className="btn sec ancho"
          to={`/movimientos?idCuenta=${idCuenta}`}
          data-prueba="prestamo-ver-movimientos"
        >
          Ver sus movimientos
        </Link>

        {prestamo.puedeCancelarElPlan && (
          <button
            className="btn sec ancho"
            type="button"
            onClick={() => navegar(`/cuentas/prestamos/${idCuenta}/plan/cancelacion`)}
            data-prueba="prestamo-cancelar-plan"
          >
            Cancelar el plan de pagos
          </button>
        )}

        {/* RN-754 · no existe descancelar: un plan cancelado se vuelve a generar. */}
        {planCancelado && prestamo.puedeGenerarElPlan && (
          <button
            className="btn ancho"
            type="button"
            onClick={() => navegar(`/cuentas/prestamos/${idCuenta}/plan`)}
            data-prueba="prestamo-regenerar-plan"
          >
            Volver a generar el plan
          </button>
        )}

        <Link className="btn sec ancho" to={`/cuentas/${idCuenta}`} data-prueba="prestamo-ver-la-cuenta">
          Ver la cuenta
        </Link>
      </div>
    </Shell>
  );
}
